interface CampaignEntry {
  country: string;
  crop?: string;
  campaignId: { prod: string; uat: string };
  webConfigName: string;
  overrideCode?: string;
}

const campaign = (prod: string, uat: string): CampaignEntry['campaignId'] => ({ prod, uat });

const entry = (
  country: string,
  prod: string,
  uat: string,
  webConfigName: string,
  options?: Pick<CampaignEntry, 'crop' | 'overrideCode'>
): CampaignEntry => ({
  country,
  campaignId: campaign(prod, uat),
  webConfigName,
  ...(options ?? {}),
});

export const standardEntries: CampaignEntry[] = [
  entry('ro', '701bG00000Rmbc4QAB', '7019M00000Q0lHjQAJ', 'www.syngenta.ro'),
  entry('pl', '701bG00000ftovqQAA', '701bh00000Rj6TaAAJ', 'www.syngenta.pl'),
  entry('hu', '701bG00000rCFHnQAO', '7019M00000Q0mTtQAJ', 'www.syngenta.hu'),
  entry('bg', '701bG00000rYpcgQAC', '00vWo000005WYtJIAW', 'www.syngenta.bg'),
  entry('de', '701bG00000Reo0IQAR', '7019M00000Q0apLQAR', 'gdpr2019.syngenta.de'),
  entry('de', '701bG00000f9UTvQAM', '701bG00000fYPh7QAG', 'gdpr2019.syngenta.de', {
    crop: 'barley',
  }),
  entry('uk', '701bG00000QxtJFQAZ', '7019M00000Q0komQAB', 'www.syngenta.ua', {
    overrideCode: 'ua',
  }),
  entry('za', '701Wo0000085S3uIAE', '7019M00000V6JYrQAN', 'www.syngenta.co.za'),
  entry('gb', '701bG00000IRwdZQAT', '701bh00000BiF8aAAF', 'www.syngenta.co.uk'),
  entry('gb', '701bG00000f7Hz1QAE', '701bh00000SA14gAAD', 'www.syngenta.co.uk', {
    crop: 'barley',
  }),
  entry('rs', '701bG00000SDbODQA1', '701bh00000GyYTiAAN', 'www.sipsyngenta.com'),
  entry('fr', '701bG00000eb23vQAA', '701bh00000QfrjkAAB', 'www.syngenta.fr'),
  entry('nl', '701bG00000Qs6HeQAJ', '701bh00000HAyE2AAL', 'www.syngenta.nl'),
  entry('es', '701bG00000q0Ks2QAE', '701bh00000X0TqVAAV', 'www.syngenta.es'),
  entry('be', '701bG00000ZBayVQAT', '701bh00000MY8lTAAT', 'www.syngenta.be'),
  entry('it', '', '701bh00000QqohjAAB', 'www.syngenta.it'),
  entry('pt', '701bG00000q0SmNQAU', '701bh00000X0X2yAAF', 'www.syngenta.pt'),
];

/**
 * Looks up the SFDC CampaignID for the given country ISO2 code and crop name.
 * Crop-specific entries (e.g. barley for DE/GB) take priority over generic ones.
 */
export function getCampaignId(countryIso2: string, cropName: string): string {
  const env = process.env.REACT_APP_ENV === 'prod' ? 'prod' : 'uat';
  const code = countryIso2.toLowerCase();
  const isBarley = cropName.toLowerCase().includes('barley');

  // Effective code: use overrideCode when present (e.g. "ua" for Ukraine entry)
  const effectiveCode = (entry: CampaignEntry) =>
    (entry.overrideCode ?? entry.country).toLowerCase();

  // 1. Crop-specific match
  if (isBarley) {
    const cropEntry = standardEntries.find((e) => effectiveCode(e) === code && e.crop === 'barley');
    if (cropEntry) {
      return cropEntry.campaignId[env];
    }
  }

  // 2. Generic country match (no crop restriction)
  const countryEntry = standardEntries.find((e) => effectiveCode(e) === code && !e.crop);
  return countryEntry?.campaignId[env] ?? '';
}
